// @flow
import queryString from 'query-string';
import { httpClient } from 'angieslist-app-platform';

export type ServiceProviderImage = {|
  id: number,
  url: string,
  caption?: string,
  title?: string,
  path?: string
|};

export type SavedImage = {|
  imageId: number,
  putUrl: string,
  getUrl: string,
  originalFilename: string,
  newFilename: string,
|};

export type AddImagesRequest = {|
  uuid: string,
  serviceProviderId: number | string,
  files: Array<File> // eslint-disable-line no-undef
|};

export type AddImagesResponse = {
  images: Array<SavedImage>,
};

export type UpdateImageRequest = {|
  uuid: string,
  serviceProviderId: number | string,
  imageId: number,
  caption: string
|};

export type UpdateImageResponse = {|
  imageId: number,
  getUrl: string,
  path: string,
  caption?: string
|};

export type RemoveImagesRequest = {|
  uuid: string,
  serviceProviderId: number | string,
  imageIds: Array<number>,
|};

export type RemoveImagesResponse = {|
|};

export type GetPhotosRequest = {|
  uuid: string,
  limit: number,
  offset: number,
|};

export type GetPhotosResponse = {
  images: Array<ServiceProviderImage>
};

export type SaveLogoResponse = {
  imageId: number,
  putUrl: string,
  getUrl: string,
  originalLogoFilename: string,
  newLogoFilename: string,
}

export type SaveLogoRequest = {
  serviceProviderId: number | string,
  logoFileName: string,
}

const mediaUrl = uuid => `/gateway/service-provider-management/resource/proapi/v1/businessprofiles/${uuid}/images`;

const uploadToS3 = (file, putUrl) =>
  fetch(putUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
    mode: 'cors',
  });

const validateImage = (serviceProviderId: string | number, imgIds: Array<number>):
  any => httpClient.post(
  `/gateway/service-provider-profile/v1/service_provider/${serviceProviderId}/image/validate`,
  {
    headers: {
      'X-ANGI-CallerType': 'ServiceProvider',
      'X-ANGI-CallerId': serviceProviderId,
    },
    body: {
      imageIds: imgIds,
    },
  },
);

async function addImages({ uuid, serviceProviderId, files }: AddImagesRequest):
  Promise<Array<ServiceProviderImage>> {
  const filenames = files.map(f => f.name);
  const { body: { images } } = (await httpClient.post(mediaUrl(uuid), {
    body: {
      filenames,
      mediaType: 'photo',
    },
  }): { body: AddImagesResponse });
  const fileMap = {};
  images.forEach((f) => { fileMap[f.originalFilename] = f; });
  const imagesUploaded = [];
  for (const file of files) { // eslint-disable-line
    const s3response = await
      uploadToS3(file, fileMap[file.name].putUrl);
    if (s3response.status === 200) {
      imagesUploaded.push(fileMap[file.name].imageId);
    }
  }

  if (imagesUploaded.length > 0) {
    await validateImage(serviceProviderId, imagesUploaded);
    return images.map(({ imageId, getUrl, newFilename }) =>
      ({ id: imageId, url: getUrl, path: newFilename }));
  }
  return [];
}

async function removeImages({ uuid, serviceProviderId, imageIds }: RemoveImagesRequest): any {
  await httpClient.del(mediaUrl(uuid), {
    body: {
      imageIds,
    },
  });
}

async function updateImage({ uuid, imageId, caption }: UpdateImageRequest):
 Promise<ServiceProviderImage> {
  const { body: { getUrl } } = (await httpClient.put(mediaUrl(uuid), {
    body: {
      imageId,
      caption,
    },
  }): { body: UpdateImageResponse });

  return { id: imageId, url: getUrl, caption };
}

const getPhotos = ({ uuid, limit, offset }: GetPhotosRequest): any =>
  httpClient.get(mediaUrl(uuid), {
    params: {
      limit,
      offset,
      mediaTypes: ['photo'],
    },
  });

const getCoverPhoto = (uuid: string): any => httpClient.get(`${mediaUrl(uuid)}?${queryString.stringify({
  mediaTypes: ['cover'],
})}`);

async function saveSinglePhoto(
  uuid: string,
  serviceProviderId: string | number,
  photoKind: string,
  file: Object,
): Promise<any> {
  const response = (await httpClient.post(
    mediaUrl(uuid),
    {
      body: {
        filenames: [file.name],
        mediaType: photoKind,
      },
    },
  ));

  const photoUploadResponse = await uploadToS3(
    file,
    response.body.images[0].putUrl,
  );

  if (photoUploadResponse.status !== 200) {
    return photoUploadResponse;
  }

  await validateImage(serviceProviderId, [response.body.images[0].imageId]);
  return response;
}

export default {
  addImages,
  updateImage,
  getPhotos,
  removeImages,
  getCoverPhoto,
  saveSinglePhoto,
};



// WEBPACK FOOTER //
// ./src/app/common/images/imagesService.js